import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { ChevronRight } from 'lucide-react';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import { FactoryStorytelling } from '@/components/about/FactoryStorytelling';
import { usePageHero } from '@/hooks/usePageHero';
import { factoryPageContentDb, factorySectionContentDb } from '@/lib/adminDb';
import { readAboutCache, writeAboutCache, type AboutDbBlock } from '@/lib/pageDataCache';

// ─── Static fallback images (только изображения — не текст) ──────────────────

interface StaticStep {
  blockKey: string;
  image: string;
  cam: string;
  bgNumber: string;
}

const STATIC_STEPS: StaticStep[] = [
  {
    blockKey: 'scale',
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfibPVOx3zES79nWOp5Eofskctwp1%2F__99aa9663.png?alt=media&token=33f2499f-a461-46fd-bdfe-29b5ef633df0',
    cam: '01', bgNumber: '01',
  },
  {
    blockKey: 'control',
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfibPVOx3zES79nWOp5Eofskctwp1%2F__ac5bb626.png?alt=media&token=418f6cde-e840-4332-8e12-7fd960718811',
    cam: '02', bgNumber: '02',
  },
  {
    blockKey: 'logistics',
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfibPVOx3zES79nWOp5Eofskctwp1%2F__1e2a676d.png?alt=media&token=5eac4078-5e35-49c1-aeaa-7b3f58a60be2',
    cam: '03', bgNumber: '03',
  },
  {
    blockKey: 'tech',
    image: 'https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FfibPVOx3zES79nWOp5Eofskctwp1%2F__3ca2542c.png?alt=media&token=4426f4d2-24a7-481b-8dcb-c492ab5185e7',
    cam: '04', bgNumber: '04',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const About: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const aboutHero = usePageHero({
    pageKey: 'about',
    lang: language,
    fallbacks: {
      title: t('page.about.title'),
    },
  });

  // Инициализируем из кэша — если кэш есть, данные появятся мгновенно
  const cachedAbout = readAboutCache(language);
  const [dbBlocks, setDbBlocks] = useState<AboutDbBlock[]>(cachedAbout?.blocks ?? []);
  const [futureTitle, setFutureTitle] = useState(cachedAbout?.futureTitle ?? '');
  const [futureText, setFutureText] = useState(cachedAbout?.futureText ?? '');

  // CRITICAL: synchronously reset state when language changes so the first
  // render after the switch shows new-language cache (or empty), never the
  // previous-language content.
  const lastLangRef = React.useRef(language);
  if (lastLangRef.current !== language) {
    lastLangRef.current = language;
    const next = readAboutCache(language);
    setDbBlocks(next?.blocks ?? []);
    setFutureTitle(next?.futureTitle ?? '');
    setFutureText(next?.futureText ?? '');
  }

  // При смене языка — сразу применяем кэш нового языка (синхронно, без задержки),
  // затем в фоне загружаем свежие данные из БД
  useEffect(() => {
    // 1. Сразу читаем кэш нового языка — если есть, обновляем state мгновенно
    const cached = readAboutCache(language);
    if (cached) {
      setDbBlocks(cached.blocks ?? []);
      setFutureTitle(cached.futureTitle ?? '');
      setFutureText(cached.futureText ?? '');
    }

    // 2. Асинхронно загружаем свежие данные из БД в фоне
    let cancelled = false;
    (async () => {
      try {
        const [blocksRes, sectionsRes] = await Promise.all([
          factoryPageContentDb.list(language),
          factorySectionContentDb.listByKey('future'),
        ]);

        if (cancelled) return;

        let newBlocks: AboutDbBlock[] = cached?.blocks ?? [];
        let newFutureTitle = cached?.futureTitle ?? '';
        let newFutureText = cached?.futureText ?? '';

        if (blocksRes.ok && Array.isArray(blocksRes.data) && blocksRes.data.length > 0) {
          newBlocks = blocksRes.data as AboutDbBlock[];
          setDbBlocks(newBlocks);
        }

        if (sectionsRes.ok && Array.isArray(sectionsRes.data)) {
          const futureSections = sectionsRes.data as any[];
          const futureSection =
            futureSections.find(s => s.lang === language) ||
            futureSections.find(s => s.lang === 'ru') ||
            futureSections[0];
          if (futureSection) {
            newFutureTitle = futureSection.title || '';
            newFutureText = futureSection.description || '';
            setFutureTitle(newFutureTitle);
            setFutureText(newFutureText);
          }
        }

        // Обновляем кэш свежими данными
        writeAboutCache(language, {
          blocks: newBlocks,
          futureTitle: newFutureTitle,
          futureText: newFutureText,
        });
      } catch {
        // Сетевая ошибка — оставляем кэшированные данные
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Используем данные из БД (или кэша). Статические данные — только для изображений-fallback
  const sections = useMemo(() => {
    return STATIC_STEPS.map((s) => {
      const fromDb = dbBlocks.find(b => b.blockKey === s.blockKey);
      return {
        number: fromDb?.bgNumber || s.bgNumber,
        title: fromDb?.title ?? '',
        subtitle: fromDb?.subtitle ?? '',
        text: fromDb?.description ?? '',
        image: fromDb?.image || s.image,
        cam: fromDb?.cam || s.cam,
      };
    });
  }, [dbBlocks]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  return (
    // Each localized slug (`o-zavode`, `zavod-haqida`, `about`) is registered
    // as a separate <Route>, so a language switch causes a full remount of
    // this page. We deliberately render the root WITHOUT an opacity-fade
    // mount animation so the user does NOT see the page briefly fade through
    // a dark/empty background on every language change. Inner sections still
    // have their whileInView animations for normal scroll behaviour.
    <div
      className="bg-background"
      style={{ overflowX: 'clip' }}
    >
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? "SARGO zavodi haqida | SPC laminat, SPC panellar va gilam plitka ishlab chiqarish" : language === 'en' ? 'About SARGO Factory | SPC Laminate, SPC Panels and Carpet Tile Production' : 'О заводе SARGO | Производство SPC-ламината, SPC-панелей и ковровой плитки'}
        description={language === 'uz'
          ? "O'zbekistondagi SARGO zavodi: ishlab chiqarish quvvati, sifat nazorati, zamonaviy texnologiyalar va mutaxassislar jamoasi."
          : language === 'en'
            ? 'SARGO factory in Uzbekistan: production capacity, quality control, modern manufacturing technologies and a team of specialists.'
            : 'Узнайте о заводе SARGO: производственные мощности, контроль качества, современные технологии и наша команда.'}
        canonical={getPagePath('about', language as Lang)}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
          { name: language === 'uz' ? 'Zavod haqida' : language === 'en' ? 'About' : 'О заводе', url: getPagePath('about', language as Lang) },
        ])}
      />
      {/* Numbered Sections 01–04 */}
      <FactoryStorytelling
        steps={sections}
        heroEyebrow={aboutHero.eyebrow}
        heroTitle={aboutHero.title}
        heroSubtitle={aboutHero.subtitle}
      />

      <div id="about-page-remaining-content" className="relative z-10 bg-background">
        {/* Future Vision Section */}
        <section
          className="relative overflow-hidden about-future-section"
          style={{ paddingTop: 'clamp(6rem, 10vh, 15rem)', paddingBottom: 'clamp(5rem, 8vh, 12rem)' }}
        >
          <div className="grid-cells opacity-50" />
          <div className="container-custom relative z-10">
            <div
              className="mx-auto text-center about-future-container"
              style={{ maxWidth: 'clamp(40rem, 60vw, 84rem)', gap: 'clamp(1.5rem, 3vh, 3.75rem)', display: 'flex', flexDirection: 'column' }}
            >
              <div className="flex justify-center">
                <div
                  className="rounded-full border border-primary flex items-center justify-center animate-bounce about-future-icon"
                  style={{ width: 'clamp(3rem, 5vw, 6rem)', height: 'clamp(3rem, 5vw, 6rem)' }}
                >
                  <ChevronRight
                    className="text-primary rotate-90"
                    style={{ width: 'clamp(1.5rem, 2.5vw, 3rem)', height: 'clamp(1.5rem, 2.5vw, 3rem)' }}
                  />
                </div>
              </div>
              <div className="space-y-3 lg:space-y-4 xl:space-y-6">
                <h2
                  className="font-extrabold uppercase tracking-tighter leading-[0.95] about-future-title"
                  style={{ fontSize: 'clamp(1.875rem, 4.2vw, 5.625rem)' }}
                >
                  {futureTitle}
                </h2>
                <p
                  className="text-white/50 leading-relaxed about-future-text"
                  style={{ fontSize: 'clamp(14px, 1.3vw, 30px)' }}
                >
                  {futureText}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
